import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Back, Empty, Inline, Primary, Secondary, Tag, inputCls, money, useQuery, when } from "@/components/backoffice/kit";

const STRIPE = { house: ["Platform account", "green"], connected: ["Connected", "green"], needs_info: ["Stripe needs info", "red"], none: ["Not connected", "grey"] };
const DOMAIN = { live: ["Live", "green"], verified: ["Verified", "green"], pending: ["Pending", "red"], none: ["13thav address", "grey"] };

function ShopDetail({ slug, back, platformMode }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => {
        setErr("");
        api.get(`/admin/shops/${slug}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    }, [slug]);
    useEffect(() => { load(); }, [load]);
    const setMode = async (mode) => {
        setBusy(true);
        try { await api.put(`/admin/shops/${slug}/site-mode`, { site_mode: mode }); toast.success(mode ? `This shop now uses ${mode}` : "Override cleared — follows the platform"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const recheck = async () => {
        setBusy(true);
        try { const { data } = await api.post(`/admin/shops/${slug}/domain/check`); toast[data.verified ? "success" : "message"](data.note); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    if (err) return <div className="grid gap-3"><Back to={back} testid="shop-back" /><Inline error={err} onRetry={load} testid="shop-error" /></div>;
    if (!d) return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="animate-spin" /></div>;
    const s = d.shop;
    const stripe = STRIPE[s.stripe] || STRIPE.none;
    const dom = DOMAIN[s.domain_status] || DOMAIN.none;
    const mode = s.site_mode || "";
    return (
        <div className="grid gap-4" data-testid="shop-detail">
            <Back to={back} testid="shop-back" />
            <header className="flex flex-wrap items-center gap-3">
                {s.logo && <img src={s.logo} alt="" className="h-12 w-12 border border-[#767676] object-contain" />}
                <div>
                    <h1 className="text-[24px] font-semibold" data-testid="shop-name">{s.name}</h1>
                    <p className="text-[15px] text-[#4d4d4d]">{s.tier_label}{s.city ? ` · ${s.city}` : ""} · {s.status}</p>
                </div>
            </header>
            <Primary to={`/stores/${s.slug}`} testid="shop-view-storefront">View storefront <ArrowUpRight size={16} /></Primary>
            <dl className="grid gap-2 text-[15px] sm:grid-cols-2" data-testid="shop-facts">
                <div className="border border-[#767676] p-3"><dt className="text-[13px] uppercase text-[#4d4d4d]">Web address</dt><dd className="break-all">{s.web_address} <Tag tone={dom[1]}>{dom[0]}</Tag></dd></div>
                <div className="border border-[#767676] p-3"><dt className="text-[13px] uppercase text-[#4d4d4d]">Stripe</dt><dd><Tag tone={stripe[1]}>{stripe[0]}</Tag></dd></div>
                <div className="border border-[#767676] p-3"><dt className="text-[13px] uppercase text-[#4d4d4d]">Plan</dt><dd>{s.tier_label}{s.plan_status ? ` · ${s.plan_status}` : ""}</dd></div>
                <div className="border border-[#767676] p-3"><dt className="text-[13px] uppercase text-[#4d4d4d]">Products</dt><dd>{s.products}</dd></div>
                <div className="border border-[#767676] p-3"><dt className="text-[13px] uppercase text-[#4d4d4d]">Last 30 days</dt><dd>{s.orders_30} orders · {money(s.sales_30)}</dd></div>
                <div className="border border-[#767676] p-3"><dt className="text-[13px] uppercase text-[#4d4d4d]">Owner</dt><dd className="break-all">{s.owner_email || "—"}{s.phone ? ` · ${s.phone}` : ""}</dd></div>
            </dl>
            <div className="flex flex-wrap gap-2" data-testid="shop-actions">
                <Secondary to={`/admin?tab=orders&f=all&shop=${s.slug}`} testid="shop-orders">Their orders</Secondary>
                {s.custom_domain && <Secondary onClick={recheck} disabled={busy} testid="shop-recheck-domain"><RefreshCw size={16} /> Re-check domain</Secondary>}
            </div>
            <section className="grid gap-2" data-testid="shop-mode">
                <h2 className="text-[16px] font-semibold">Simple | Classic for this shop</h2>
                <p className="text-[14px] text-[#4d4d4d]">Platform default is <b>{platformMode}</b>. An override applies to this shop's site and back office only.</p>
                <div className="flex flex-wrap gap-2">
                    {[["", "Follow platform"], ["simple", "Simple"], ["classic", "Classic"]].map(([v, l]) => (
                        <button key={v} onClick={() => setMode(v || null)} disabled={busy || mode === v} data-testid={`shop-mode-${v || "platform"}`} aria-pressed={mode === v}
                            className={`min-h-[44px] border px-3 text-[15px] font-medium ${mode === v ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white"}`}>{l}</button>
                    ))}
                </div>
            </section>
            {s.custom_domain && d.domain && (
                <section className="text-[15px]" data-testid="shop-domain">
                    <h2 className="text-[16px] font-semibold">Domain</h2>
                    <p>{d.domain.custom_domain} · {dom[0]}{d.domain.checked_at ? ` · checked ${when(d.domain.checked_at)}` : ""}</p>
                    {d.domain.dns_error && <p className="text-[#b3261e]">{d.domain.dns_error}</p>}
                    {(d.domain.records || []).length > 0 && (
                        <ul className="mt-1 border border-[#767676] text-[14px]">
                            {d.domain.records.map((r, n) => <li key={n} className="flex gap-3 border-b border-[#767676] px-2 py-1 last:border-b-0"><b>{r.type}</b><span>{r.name}</span><span className="break-all">{r.value}</span></li>)}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
}

/** 8.12 Shops — every tenant on one list, search, tap → detail (View storefront, their orders, mode override, re-check domain). */
export default function ShopsScreen() {
    const { params, set, pathname } = useQuery();
    const slug = params.get("shop");
    const q = params.get("q") || "";
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [typed, setTyped] = useState(q);
    const load = useCallback(() => {
        setErr("");
        api.get("/admin/shops", { params: { q } }).then(({ data: d }) => setData(d)).catch((e) => { setData({ shops: [] }); setErr(formatApiError(e.response?.data?.detail)); });
    }, [q]);
    useEffect(() => { load(); }, [load]);
    if (slug) {
        const p = new URLSearchParams(params); p.delete("shop");
        return <ShopDetail slug={slug} back={`${pathname}?${p}`} platformMode={data?.site_mode || "simple"} />;
    }
    const rows = data?.shops || [];
    return (
        <div className="grid gap-4" data-testid="shops-screen">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-[22px] font-semibold">Shops <span className="text-[16px] font-normal text-[#4d4d4d]">{rows.length}</span></h1>
                <form onSubmit={(e) => { e.preventDefault(); set({ tab: "shops", q: typed.trim() }); }} className="flex gap-2">
                    <input className={inputCls} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Name, address or city" aria-label="Search shops" data-testid="shops-search" />
                    <Secondary type="submit" testid="shops-search-go">Search</Secondary>
                </form>
            </div>
            <Inline error={err} onRetry={load} testid="shops-error" />
            {data === null ? <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="animate-spin" /></div>
                : rows.length === 0 ? <Empty title={q ? `No shop matches “${q}”.` : "No shops yet."} testid="shops-empty" />
                    : (
                        <ul className="grid gap-2" data-testid="shops-rows">
                            {rows.map((s) => {
                                const stripe = STRIPE[s.stripe] || STRIPE.none;
                                const dom = DOMAIN[s.domain_status] || DOMAIN.none;
                                return (
                                    <li key={s.slug}>
                                        <button onClick={() => set({ tab: "shops", shop: s.slug })} data-testid={`shop-row-${s.slug}`}
                                            className="grid w-full grid-cols-[48px_minmax(0,1fr)] gap-3 border border-[#1a1a1a] bg-white p-3 text-left sm:grid-cols-[48px_minmax(0,1fr)_auto]">
                                            <div className="h-12 w-12 border border-[#767676] bg-white">{s.logo && <img src={s.logo} alt="" className="h-full w-full object-contain" />}</div>
                                            <div className="min-w-0">
                                                <p className="text-[16px] font-semibold">{s.name} <span className="font-normal text-[#4d4d4d]">· {s.tier_label}</span></p>
                                                <p className="break-all text-[14px] text-[#4d4d4d]">{s.web_address}</p>
                                                <div className="mt-1 flex flex-wrap gap-1.5">
                                                    <Tag tone={dom[1]}>{dom[0]}</Tag>
                                                    <Tag tone={stripe[1]}>{stripe[0]}</Tag>
                                                    {s.site_mode && <Tag tone="ink">{s.site_mode}</Tag>}
                                                    {s.status !== "active" && <Tag tone="red">{s.status}</Tag>}
                                                </div>
                                            </div>
                                            <div className="col-span-2 text-[14px] text-[#4d4d4d] sm:col-span-1 sm:text-right">
                                                <p>{s.products} products</p>
                                                <p>{s.orders_30} orders · {money(s.sales_30)} <span className="text-[12px]">30 days</span></p>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
        </div>
    );
}
